import { useCallback, useEffect, useMemo, useState } from "react";

import { useToasts } from "react-toast-notifications";

import { useUser } from "@components/user-context";
import { prettyLogEvent } from "@internal/analytics";
import { Pages } from "@north-beam/nb-client-side/constants";
import { PrimaryButton, TertiaryButton } from "@shared/buttons";
import { Modal } from "@shared/modal";

import { useSavedViews } from "../hooks/saved-view";

import { Input } from "./ca-top-bar";
import { Button } from "./saved-views-control";

export function CreateSavedViewModal({
  open,
  closeModal,
}: {
  open: boolean;
  closeModal: () => void;
}) {
  const {
    availableSavedViews,
    createNewSavedView,
    loadingSavedViewUpsert,
    setSelectedSaveView,
  } = useSavedViews();
  const { user } = useUser();
  const [savedViewName, setSavedViewName] = useState<string>();
  const [hasError, setHasError] = useState(false);
  const [hasSubmitted, setHasSubmitted] = useState(false);

  const allNamesSet = useMemo(
    () => new Set(availableSavedViews.map((v) => v.name)),
    [availableSavedViews],
  );
  const trimmedSavedViewName = savedViewName?.trim();

  useEffect(() => {
    if (!hasSubmitted) {
      return;
    }
    const foundSavedView = availableSavedViews.find(
      (view) => view.name === trimmedSavedViewName,
    );
    if (foundSavedView) {
      setSavedViewName(undefined);
      setSelectedSaveView(foundSavedView.id);
      setHasSubmitted(false);
      closeModal();
    }
  }, [
    trimmedSavedViewName,
    availableSavedViews,
    setSelectedSaveView,
    closeModal,
    hasSubmitted,
  ]);

  const onClose = useCallback(() => {
    closeModal();
    setSavedViewName(undefined);
  }, [closeModal]);

  const toast = useToasts();

  return (
    <Modal
      isOpen={open}
      onClose={onClose}
      titleContent={
        <div className="text-lg font-semibold">
          Save current page selection as a new view
        </div>
      }
    >
      <div className="py-4">
        <div className="text-sm ">SAVED VIEW NAME</div>
        <Input
          className="mt-2 w-[15rem]"
          value={savedViewName}
          onChange={(e) => {
            const text = e.target.value;
            setSavedViewName(text);
            setHasError(allNamesSet.has(text.trim()));
          }}
          placeholder="New saved view name..."
          id="search-metrics"
        />
        {hasError && (
          <div className="text-danger text-sm">Name already exists</div>
        )}
      </div>
      <div className="flex items-center justify-end gap-2">
        <TertiaryButton onClick={onClose}>Cancel</TertiaryButton>
        <PrimaryButton
          loading={loadingSavedViewUpsert}
          isDisabled={
            !trimmedSavedViewName || allNamesSet.has(trimmedSavedViewName)
          }
          onClick={async () => {
            if (trimmedSavedViewName) {
              await createNewSavedView(trimmedSavedViewName);
              prettyLogEvent.onSubmit({
                user,
                page: Pages.CREATIVE_ANALYTICS,
                domain: "Saved Views",
                additionalActionInfo: "new-modal",
                data: {
                  name: trimmedSavedViewName,
                },
              });
              setHasError(false);
              setHasSubmitted(true);
              toast.addToast("Saved view created", { appearance: "success" });
            }
          }}
        >
          Save
        </PrimaryButton>
      </div>
    </Modal>
  );
}
